// Generated from src/collectorTemplate.ts.
/** Canonical physical card contract. Contexts may arrange cards, never rewrite their faces. */
export const COLLECTOR_TEMPLATE_VERSION = "bilt.collector.v2";
export const collectorMaterial = {
    ratio: 5 / 7,
    width: 280,
    height: 392,
    radius: 14,
    ink: "#F4F3EF",
    secondary: "#B1B4BA",
    stock: "#101215",
    recess: "#15181C",
    edge: "#303237",
    accent: "#FF9A43",
    bronze: "#A7774C",
    bronzeInk: "#FFE0BC",
    bronzeTop: "#423023",
    bronzeBottom: "#1A1918",
    redline: "#FF797C",
    certification: "#F4D26C",
    hitTarget: 44,
};
export const collectorTemplates = {
    vehicle: {
        front: ["modification_count", "cost", "power", "torque", "redline"],
        back: ["engine", "transmission", "drivetrain", "chassis"],
        heading: "Technical record",
    },
    event: {
        front: ["start_at", "venue", "city"],
        back: ["end_at", "timezone", "host", "category"],
        heading: "Schedule & organizer",
    },
    club: {
        front: ["region", "focus"],
        back: ["founded_year"],
        heading: "Community charter",
    },
    shop: {
        front: ["specialties", "city"],
        back: ["services", "address"],
        heading: "Service record",
    },
    place: {
        front: ["category", "city"],
        back: ["offering", "exhibition", "address", "operator", "access"],
        heading: "Visitor notes",
    },
    booth: {
        front: ["campaign_title", "zone"],
        back: ["exhibitor", "host", "category", "description"],
        heading: "Exhibitor sheet",
    },
    activation: {
        front: ["category", "result"],
        back: ["host", "session", "description"],
        heading: "Session record",
    },
    destination: {
        front: ["position", "campaign_title"],
        back: ["place_name", "description"],
        heading: "Route note",
    },
    checkpoint: {
        front: ["position", "campaign_title"],
        back: ["criterion", "optional"],
        heading: "Published criterion",
    },
    commemorative: {
        front: ["occasion", "issuer"],
        back: ["story", "credits"],
        heading: "Edition colophon",
    },
};
const labels = {
    modification_count: "Mods",
    cost: "Cost",
    power: "Power",
    torque: "Torque",
    redline: "RPM",
    founded_year: "Founded",
    place_name: "Linked place",
    start_at: "Starts",
    end_at: "Ends",
    campaign_title: "Program",
    optional: "Requirement",
    position: "Stop",
    drivetrain: "Drive",
};
/** Empty is distinct from zero. Objects, non-finite numbers and arbitrary values never become facts. */
export function collectorValue(value) {
    if (typeof value === "number")
        return Number.isFinite(value) ? String(value) : "-";
    if (typeof value !== "string")
        return "-";
    return value.trim() || "-";
}
export function collectorTemplate(kind) {
    return Object.prototype.hasOwnProperty.call(collectorTemplates, kind)
        ? collectorTemplates[kind]
        : null;
}
export function collectorFacts(kind, face, facts, complete = false) {
    const definition = collectorTemplate(kind);
    if (!definition)
        return [];
    return definition[face]
        .flatMap((key) => {
        const raw = facts?.[key];
        let value = collectorValue(raw);
        if (value === "-" && !(kind === "vehicle" && face === "front"))
            return [];
        if (value !== "-" && key === "power")
            value += ` ${facts?.power_unit === "WHP" ? "WHP" : "HP"}`;
        if (value !== "-" && key === "torque")
            value += " lb-ft";
        if (value !== "-" && key === "redline" && Number.isFinite(Number(raw)))
            value = Number(raw).toLocaleString("en-US");
        if (value !== "-" && key === "cost" && Number.isFinite(Number(raw))) {
            const n = Number(raw);
            value =
                n >= 1000
                    ? `$${Math.round(n / 1000)}K`
                    : `$${n.toLocaleString("en-US")}`;
        }
        return [
            {
                key,
                label: labels[key] ||
                    key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
                value,
            },
        ];
    })
        .slice(0, complete || face === "front" ? undefined : 4);
}
/** Preserve issue identity and explicit feature order; never merge awards solely by title. */
export function collectorRecognition(input, vehicleId) {
    if (!Array.isArray(input))
        return [];
    const seen = new Set();
    const awards = new Set();
    return input
        .filter((v) => !!v && typeof v === "object")
        .filter((v) => v.is_active !== false &&
        v.is_active !== 0 &&
        v.revoked_at == null &&
        (!vehicleId || !v.vehicle_id || Number(v.vehicle_id) === vehicleId))
        .slice()
        .sort((a, b) => {
        const position = (v) => typeof v.featured_position === "number"
            ? v.featured_position
            : Number.MAX_SAFE_INTEGER;
        const rank = (v) => {
            const design = v.design;
            return [
                "major",
                "award",
                "participation",
                "affiliation",
                "milestone",
            ].indexOf(String(design?.prestige_tier));
        };
        const prestige = (v) => rank(v) < 0 ? 99 : rank(v);
        return (position(a) - position(b) ||
            prestige(a) - prestige(b) ||
            String(b.issued_at || "").localeCompare(String(a.issued_at || "")) ||
            String(a.public_id || a.id).localeCompare(String(b.public_id || b.id)));
    })
        .flatMap((v) => {
        const id = typeof v.public_id === "string"
            ? v.public_id
            : typeof v.id === "number"
                ? String(v.id)
                : "";
        if (!id || seen.has(id))
            return [];
        const award = typeof v.award_id === "number" && v.award_id > 0 ? v.award_id : null;
        if (award && awards.has(award))
            return [];
        if (award)
            awards.add(award);
        seen.add(id);
        const d = v.design && typeof v.design === "object"
            ? v.design
            : {};
        const title = collectorValue(d.name || d.short_label || v.title);
        if (title === "-")
            return [];
        const issuer = v.issuer && typeof v.issuer === "object"
            ? v.issuer
            : {};
        const entity = (issuer.event || issuer.club);
        return [
            {
                id,
                title,
                category: collectorValue(d.badge_category),
                issuer: typeof entity?.title === "string"
                    ? entity.title
                    : typeof entity?.name === "string"
                        ? entity.name
                        : null,
                issuedAt: typeof v.issued_at === "string" ? v.issued_at : null,
                artwork: typeof d.custom_logo_path === "string" ? d.custom_logo_path : null,
                source: v,
            },
        ];
    });
}
export const collectorBoundaries = {
    vehicle: "",
    event: "A collectible occasion record. Not admission.",
    club: "Collection does not confer club membership.",
    shop: "A visit record does not prove a service or purchase.",
    place: "Visit evidence and program qualification are separate.",
    booth: "A booth visit does not grant marketing consent.",
    activation: "Results appear only when supplied by an approved source.",
    destination: "This route stop and its linked place keep separate identities.",
    checkpoint: "Qualification follows the published program criterion.",
    commemorative: "No physical visit is implied by this commemorative.",
};
/** Select content before platform rendering. Overflow remains in the separately readable record. */
export function collectorPresentation(kind, facts, badges, sourceStory) {
    const definition = collectorTemplate(kind);
    const recognition = collectorRecognition(badges);
    const frontRecognition = kind === "vehicle" ? recognition.slice(0, 5) : [];
    const reverseRecognition = recognition.slice(frontRecognition.length, frontRecognition.length + 2);
    const front = collectorFacts(kind, "front", facts);
    const back = collectorFacts(kind, "back", facts);
    const story = collectorValue(sourceStory);
    const reverseStory = reverseRecognition.length === 0 &&
        story !== "-" &&
        ![...front, ...back].some((f) => f.value === story)
        ? story
        : null;
    return {
        version: COLLECTOR_TEMPLATE_VERSION,
        heading: definition?.heading || "Collected record",
        front,
        back,
        frontRecognition,
        reverseRecognition,
        reverseStory,
        density: recognition.length || back.length > 2
            ? "rich"
            : back.length || front.some((f) => f.value !== "-")
                ? "standard"
                : "minimal",
        boundary: collectorBoundaries[kind] || "",
    };
}
/** Issued badge rail art: the same 64-unit roundel, source palette and short label on both platforms. */
export function collectorBadgeMark(design) {
    const color = (v, fallback) => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
    const tier = design.prestige_tier;
    return {
        primary: color(design.primary_color, "#1A1A1A"),
        secondary: color(design.secondary_color, "#F97316"),
        accent: color(design.accent_color, "#FAFAFA"),
        label: String(design.short_label || design.name || "")
            .trim()
            .slice(0, 3),
        strokeWidth: tier === "major" ? 2.5 : tier === "award" ? 2 : 1.25,
        strokeOpacity: tier === "major" ? 0.95 : tier === "award" ? 0.8 : 0.55,
        size: 26,
    };
}
/** Stable SVG silhouettes — viewBox 0 0 64 64, centered content area. */
export function collectorBadgePath(shape) {
    switch (shape) {
        case 'shield':
            return 'M32 4 L56 12 V34 C56 48 44 56 32 60 C20 56 8 48 8 34 V12 Z';
        case 'hexagon':
            return 'M20 6 H44 L58 32 L44 58 H20 L6 32 Z';
        case 'pennant':
            return 'M8 8 H56 L48 56 H8 Z';
        case 'crest':
            return 'M32 4 L54 12 V30 C54 46 42 54 32 60 C22 54 10 46 10 30 V12 Z';
        case 'plaque':
            return 'M10 8 H54 L60 16 V48 L54 56 H10 L4 48 V16 Z';
        case 'rounded_rectangle':
            return 'M12 8 H52 A4 4 0 0 1 56 12 V52 A4 4 0 0 1 52 56 H12 A4 4 0 0 1 8 52 V12 A4 4 0 0 1 12 8 Z';
        case 'circle':
        case 'roundel':
        default:
            return 'M32 4 A28 28 0 1 1 31.9 4 Z';
    }
}
